import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { cn } from "../lib/utils";

type ScrollState = {
  canScrollBackward: boolean;
  canScrollForward: boolean;
};

const EDGE_TOLERANCE = 2;

export function getHorizontalScrollState({
  scrollLeft,
  scrollWidth,
  clientWidth,
}: Pick<
  HTMLElement,
  "scrollLeft" | "scrollWidth" | "clientWidth"
>): ScrollState {
  return {
    canScrollBackward: scrollLeft > EDGE_TOLERANCE,
    canScrollForward: scrollLeft + clientWidth < scrollWidth - EDGE_TOLERANCE,
  };
}

export function HorizontalPostShelf({
  id,
  label,
  contentVersion,
  header,
  meta,
  children,
  fill = false,
}: {
  id: string;
  label: string;
  contentVersion: number;
  header: ReactNode;
  meta: ReactNode;
  children: ReactNode;
  /** One item spanning the row, e.g. while posts are still being planned. */
  fill?: boolean;
}) {
  const shelfRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState<ScrollState>({
    canScrollBackward: false,
    canScrollForward: false,
  });

  const updateScrollState = useCallback(() => {
    const shelf = shelfRef.current;
    if (!shelf) return;

    const nextState = getHorizontalScrollState(shelf);
    setScrollState((currentState) =>
      currentState.canScrollBackward === nextState.canScrollBackward &&
      currentState.canScrollForward === nextState.canScrollForward
        ? currentState
        : nextState,
    );
  }, []);

  useEffect(() => {
    const shelf = shelfRef.current;
    if (!shelf) return;

    updateScrollState();
    const animationFrame = window.requestAnimationFrame(updateScrollState);
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(updateScrollState);
    resizeObserver?.observe(shelf);
    Array.from(shelf.children).forEach((child) =>
      resizeObserver?.observe(child),
    );
    window.addEventListener("resize", updateScrollState);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateScrollState);
    };
  }, [contentVersion, updateScrollState]);

  const scrollOnePage = (direction: -1 | 1) => {
    const shelf = shelfRef.current;
    if (!shelf) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    shelf.scrollBy({
      left: direction * Math.max(shelf.clientWidth - 16, 240),
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  const handleShelfKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
    event.preventDefault();
    scrollOnePage(event.key === "ArrowLeft" ? -1 : 1);
  };

  const hasOverflow =
    scrollState.canScrollBackward || scrollState.canScrollForward;

  return (
    <>
      <div className="mb-3 flex items-end justify-between gap-4">
        {header}
        <div className="flex shrink-0 items-center gap-2.5">
          {meta}
          {hasOverflow && (
            <div className="hidden overflow-hidden rounded-lg border border-border bg-background shadow-sm sm:flex">
              <button
                type="button"
                aria-controls={id}
                aria-label={`Show previous posts from ${label}`}
                disabled={!scrollState.canScrollBackward}
                onClick={() => scrollOnePage(-1)}
                className="flex h-11 w-11 items-center justify-center border-r border-border text-foreground transition-colors hover:bg-muted focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-default disabled:text-muted-foreground/40 disabled:hover:bg-transparent"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-controls={id}
                aria-label={`Show more posts from ${label}`}
                disabled={!scrollState.canScrollForward}
                onClick={() => scrollOnePage(1)}
                className="flex h-11 w-11 items-center justify-center text-foreground transition-colors hover:bg-muted focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:cursor-default disabled:text-muted-foreground/40 disabled:hover:bg-transparent"
              >
                <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      </div>

      <div
        className={cn(
          "relative",
          contentVersion > 1 && "-mr-4 sm:mr-0",
          contentVersion > 4 && "lg:-mr-6",
        )}
      >
        <div
          ref={shelfRef}
          id={id}
          role="region"
          aria-label={`${label} posts`}
          tabIndex={hasOverflow ? 0 : undefined}
          onScroll={updateScrollState}
          onKeyDown={handleShelfKeyDown}
          className={cn(
            "grid snap-x snap-mandatory grid-flow-col gap-4 overflow-x-auto pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            fill
              ? "auto-cols-[100%]"
              : cn(
                  "sm:auto-cols-[14rem] sm:pr-0",
                  contentVersion > 1 ? "auto-cols-[78%] pr-4" : "auto-cols-[100%]",
                  contentVersion > 4
                    ? "lg:auto-cols-[calc((100%_-_4rem)/4.2)] lg:pr-6"
                    : "lg:auto-cols-[calc((100%_-_3rem)/4)]",
                ),
          )}
        >
          {children}
        </div>

        {scrollState.canScrollBackward && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-3 left-0 z-10 w-8 bg-gradient-to-r from-background/80 to-transparent lg:w-6"
          />
        )}

        {scrollState.canScrollForward && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-3 right-0 z-10 w-6 bg-gradient-to-l from-background/80 to-transparent sm:w-10 lg:w-6"
          />
        )}
      </div>
    </>
  );
}
